/**
 * Thai translations. Keys mirror `en.ts`. Any key omitted here falls back to
 * English automatically via `useT()`.
 */
import type { TranslationKeys } from './en';

// Widen string-literal leaves to `string` so translations don't have to match
// the exact English wording (which `typeof en as const` freezes as literals).
type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends string
    ? string
    : T[K] extends object
      ? DeepPartial<T[K]>
      : T[K];
};

export const th: DeepPartial<TranslationKeys> = {
  common: {
    back: 'ย้อนกลับ',
    cancel: 'ยกเลิก',
    save: 'บันทึก',
    delete: 'ลบ',
    edit: 'แก้ไข',
    loading: 'กำลังโหลด…',
    search: 'ค้นหา',
    close: 'ปิด',
    submit: 'ส่ง',
    all: 'ทั้งหมด',
    error_generic: 'เกิดข้อผิดพลาด',
  },

  nav: {
    home: 'หน้าแรก',
    explore: 'สำรวจ',
    trending: 'กำลังมาแรง',
    all_tags: 'แท็กทั้งหมด',
    collections: 'คอลเลกชัน',
    following: 'กำลังติดตาม',
    saved: 'ที่บันทึกไว้',
    my_prompts: 'Prompt ของฉัน',
    admin: 'ผู้ดูแล',
    new_prompt: 'สร้าง Prompt',
    sign_in: 'เข้าสู่ระบบ',
    profile: 'โปรไฟล์',
    libraries: 'หมวดหมู่',
    map: 'แผนที่โลก',
    my_reports: 'รายงานของฉัน',
  },

  navbar: {
    explore: 'สำรวจ',
    search_placeholder: 'Prompt แท็ก หรือผู้เขียน',
    clear_search: 'ล้างการค้นหา',
    sign_in: 'เข้าสู่ระบบ',
    new_prompt: 'สร้าง Prompt',
    account_menu: 'เมนูบัญชี',
    view_profile: 'ดูโปรไฟล์',
    my_prompts: 'Prompt ของฉัน',
    saved: 'ที่บันทึกไว้',
    logout: 'ออกจากระบบ',
  },

  verify: {
    eyebrow: 'ยืนยันอีเมล',
    checking: 'กำลังยืนยัน…',
    one_moment: 'รอสักครู่',
    success_title: 'ยืนยันอีเมลแล้ว',
    success_body: 'ตอนนี้คุณโพสต์ Prompt สาธารณะได้แล้ว',
    error_title: 'ยืนยันไม่ได้',
    expired_hint: 'ลิงก์อาจหมดอายุ — เข้าสู่ระบบเพื่อขอลิงก์ใหม่',
    back_home: 'กลับสู่แกลเลอรี',
    sign_in: 'เข้าสู่ระบบ',
    resend: 'ส่งลิงก์ใหม่',
    resent: 'ส่งลิงก์ใหม่แล้ว — ตรวจอีเมลของคุณ',
  },

  forgot: {
    eyebrow: 'รีเซ็ตรหัสผ่าน',
    title: 'ลืมรหัสผ่าน?',
    subtitle: 'กรอกอีเมลที่ใช้สมัคร เราจะส่งลิงก์รีเซ็ตให้',
    send_link: 'ส่งลิงก์รีเซ็ต',
    sending: 'กำลังส่ง…',
    success_body: 'ถ้าอีเมลนี้มีในระบบ ลิงก์รีเซ็ตกำลังถูกส่งไป ตรวจกล่องจดหมาย (และโฟลเดอร์สแปม)',
    expiry_note: 'ลิงก์หมดอายุใน 30 นาที',
    back_to_login: 'กลับไปเข้าสู่ระบบ',
    error_generic: 'ส่งลิงก์ไม่ได้ ลองใหม่อีกครั้ง',
  },

  reset: {
    eyebrow: 'รีเซ็ตรหัสผ่าน',
    title: 'ตั้งรหัสผ่านใหม่',
    subtitle: 'เลือกรหัสที่คุณจำได้ อย่างน้อย 8 ตัวอักษร',
    new_password: 'รหัสผ่านใหม่',
    confirm_password: 'ยืนยันรหัสผ่านใหม่',
    set_new_password: 'บันทึกรหัสผ่านใหม่',
    saving: 'กำลังบันทึก…',
    success_toast: 'รีเซ็ตรหัสผ่านแล้ว — เข้าสู่ระบบให้คุณอัตโนมัติ',
    error_no_token: 'หน้านี้ต้องการ token รีเซ็ต กรุณาเปิดผ่านลิงก์ในอีเมล',
    error_too_short: 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร',
    error_mismatch: 'รหัสผ่านไม่ตรงกัน',
    error_generic: 'รีเซ็ตไม่สำเร็จ — ลิงก์อาจหมดอายุ ขอลิงก์ใหม่',
    back_to_login: 'กลับไปเข้าสู่ระบบ',
  },

  reports: {
    eyebrow: 'การกำกับดูแล',
    title: 'รายงานของฉัน',
    subtitle: 'รายงานที่คุณส่งและคำตัดสินของผู้ดูแล',
    empty: 'คุณยังไม่ได้ส่งรายงาน',
    signin_required: 'เข้าสู่ระบบเพื่อดูรายงานที่คุณส่งไว้',
    filed_on: 'ส่งเมื่อ {date}',
    resolution_note: 'หมายเหตุของผู้ดูแล',
    status_open: 'รอการตรวจสอบ',
    status_resolved: 'ดำเนินการแล้ว',
    status_dismissed: 'ปฏิเสธ',
    reason_spam: 'สแปม',
    reason_abuse: 'การคุกคาม',
    reason_copyright: 'ละเมิดลิขสิทธิ์',
    reason_nsfw: 'เนื้อหาผู้ใหญ่',
    reason_other: 'อื่นๆ',
  },

  map: {
    title: 'แผนที่โลก',
    stats: '{posts} Prompt · {countries} ประเทศ',
    no_prompts: 'ยังไม่มี Prompt',
    n_prompts: '{count} Prompt',
    one_prompt: '1 Prompt',
    legend_less: 'น้อย',
    legend_more: 'มาก ({max})',
    zoom_in: 'ซูมเข้า',
    zoom_out: 'ซูมออก',
    zoom_reset: 'รีเซ็ตการซูม',
    close_panel: 'ปิด',
  },

  welcome: {
    eyebrow: 'ยินดีต้อนรับ',
    headline: 'สวัสดี {name} — เริ่มได้ที่ 3 จุดนี้',
    body: 'บันทึก Prompt ที่ชอบ ติดตามผู้สร้าง และแชร์ผลงานของตัวเอง ไอคอนบุ๊กมาร์กบนทุก Prompt จะสร้างคลังส่วนตัวให้คุณ — เริ่มจากที่กำลังมาแรง หรือเลือกหมวดที่ตรงกับงานของคุณ',
    cta_trending: 'ดูที่กำลังมาแรง',
    cta_explore: 'สำรวจหมวดหมู่',
    cta_share: 'แชร์ Prompt แรกของคุณ',
    dismiss_aria: 'ปิดข้อความต้อนรับ',
    dismiss_title: 'ไม่ต้องแสดงอีก',
  },

  topbar: {
    search_placeholder: 'ค้นหา Prompt แท็ก หรือผู้ใช้',
    sign_up: 'สมัครสมาชิก',
    view_profile: 'ดูโปรไฟล์',
    logout: 'ออกจากระบบ',
    language: 'ภาษา',
    theme_toggle_light: 'เปลี่ยนเป็นโหมดสว่าง',
    theme_toggle_dark: 'เปลี่ยนเป็นโหมดมืด',
    my_collections: 'คอลเลกชั่นของฉัน',
    my_prompts: 'Prompt ของฉัน',
  },

  home: {
    hero_title_before: 'ศิลปะแห่งการเขียน',
    hero_title_em: 'Prompt',
    hero_title_after: 'ชั้นเยี่ยม',
    hero_tagline: 'แกลเลอรี Prompt คัดสรรสำหรับครีเอเตอร์ นักพัฒนา และนักเขียน — ค้นหา บันทึก และดัดแปลง Prompt ที่ใช้งานได้จริง',
    start_exploring: 'เริ่มสำรวจ',
    share_a_prompt: 'แบ่งปัน Prompt',
    stat_prompts_shared: 'Prompt ทั้งหมด',
    stat_copies_month: 'คัดลอกเดือนนี้',
    stat_creators: 'ผู้สร้าง',
    section_eyebrow: 'คอลเลกชัน',
    featured_prompts: 'Prompt แนะนำ',
    category_prompts: 'Prompt หมวด {category}',
    sort_featured: 'แนะนำ',
    sort_newest: 'ใหม่ล่าสุด',
    sort_top: 'ยอดนิยม',
    all_categories: 'ทุกหมวด',
    category_art: 'ศิลปะ',
    category_writing: 'งานเขียน',
    category_coding: 'โค้ด',
    category_business: 'ธุรกิจ',
    category_education: 'การศึกษา',
    clear_tags: 'ล้าง',
    browse_tags: 'ดูแท็ก',
    search_placeholder: 'ค้นหา Prompt...',
    layout_masonry: 'เลย์เอาต์ Masonry',
    layout_uniform: 'กริดเท่ากัน',
    layout_list: 'มุมมองรายการ',
    empty_no_prompts: 'ไม่พบ Prompt ที่ตรงกับการค้นหา',
    empty_try_clearing: 'ลองล้างฟิลเตอร์หรือค้นหาคำอื่น',
    load_more: 'โหลดเพิ่ม',
  },

  trending: {
    eyebrow: 'กำลังมาแรง',
    title_before: 'สิ่งที่โลกกำลัง',
    title_em: 'บันทึก',
    title_after: '.',
    tagline: 'Prompt ที่มีแรงส่งมากที่สุด — อัปเดตทุกชั่วโมง',
    period_day: 'วันนี้',
    period_week: 'สัปดาห์นี้',
    period_month: 'เดือนนี้',
    ranking_eyebrow: 'อันดับ',
    ranking_subtitle: 'ถัดจาก 3 อันดับแรก',
    also_trending: 'มาแรงอื่นๆ',
    full_grid: 'แกลเลอรีเต็ม',
    empty: 'ยังไม่มี Prompt ที่กำลังมาแรง — กลับมาดูอีกครั้งภายหลัง',
    saves_label: 'บันทึก',
  },

  following: {
    eyebrow: 'คนที่คุณติดตาม',
    title_before: 'คนที่คุณ',
    title_em: 'ติดตาม',
    tagline: 'Prompt ใหม่ๆ จากผู้สร้างที่คุณติดตาม — ใหม่สุดก่อน',
    sort_newest: 'ใหม่สุด',
    sort_top_rated: 'ยอดนิยม',
    sort_latest: 'ล่าสุด',
    empty_title: 'ฟีดของคุณเงียบอยู่',
    empty_body: 'ลองติดตามผู้สร้างด้านล่าง แล้ว Prompt ใหม่ๆ ของพวกเขาจะมาแสดงที่นี่',
    suggest_heading: 'ผู้สร้างที่คนกำลังชื่นชอบ',
    suggest_home: 'ผู้สร้างที่น่าติดตาม',
    see_feed: 'ดูฟีดของคุณ →',
    all_set: 'เรียบร้อยแล้ว!',
    your_feed: 'ฟีดของคุณ',
    following_count_one: '{count} ผู้สร้าง',
    following_count_other: '{count} ผู้สร้าง',
    new_prompts_summary: '{count} Prompt ใหม่จากคนที่คุณเชื่อใจ · ไม่มีอัลกอริทึม เรียงตามเวลาเท่านั้น',
    discover: 'ค้นหา',
    fresh_from_follows: 'ใหม่จากคนที่คุณติดตาม',
    prompt_count_one: '{count} Prompt',
    prompt_count_other: '{count} Prompt',
    empty_no_new_title: 'ยังไม่มี Prompt ใหม่',
    empty_no_new_body: 'ผู้สร้างที่คุณติดตามยังไม่ได้แชร์อะไรสาธารณะเมื่อเร็วๆ นี้',
    sidebar_who_to_follow: 'ใครน่าติดตาม',
    sidebar_refresh: 'รีเฟรช',
    sidebar_quote: '“การติดตามคือสัญญาณ อัลกอริทึมคือสัญญาณรบกวน”',
    sidebar_quote_attr: '— Prompt Save weekly',
    headline_following_em: 'กำลังติดตาม',
  },

  auth: {
    welcome_back: 'ยินดีต้อนรับกลับ',
    create_account: 'สร้างบัญชี',
    sign_in: 'เข้าสู่ระบบ',
    sign_up: 'สมัครสมาชิก',
    signing_in: 'กำลังเข้าสู่ระบบ…',
    creating_account: 'กำลังสร้างบัญชี…',
    continue_google: 'ดำเนินการต่อด้วย Google',
    or: 'หรือ',
    name: 'ชื่อ',
    username: 'ชื่อผู้ใช้',
    email: 'อีเมล',
    password: 'รหัสผ่าน',
    hint_no_spaces: 'ห้ามเว้นวรรค',
    hint_min_8: 'อย่างน้อย 8 ตัวอักษร',
    have_account: 'มีบัญชีอยู่แล้ว?',
    new_to_site: 'ยังไม่มีบัญชี?',
  },

  login: {
    back_to_gallery: 'กลับสู่แกลเลอรี',
    quote: '“Prompt ที่ดีคือหน่วยที่เล็กที่สุดของจินตนาการที่นำไปใช้ได้จริง”',
    quote_attr: '— จากแถลงการณ์ของ Prompt Save',
    stat_prompts: 'Prompt',
    stat_creators: 'ผู้สร้าง',
    stat_copies_per_month: 'คัดลอก / เดือน',
    welcome_back_pre: 'ยินดีต้อนรับ',
    welcome_back_em: 'กลับ',
    create_pre: 'สร้าง',
    create_em: 'บัญชีของคุณ',
    sub_signup: 'เริ่มบันทึก แชร์ และดัดแปลง Prompt',
    sub_login: 'เข้าสู่ระบบเพื่อใช้งานคลัง Prompt Save ของคุณ',
    name_placeholder: 'ชื่อของคุณ',
    username_placeholder: 'ชื่อผู้ใช้',
    email_placeholder: 'you@domain.com',
    password_placeholder: '••••••••',
    forgot: 'ลืมรหัสผ่าน?',
    create_account_btn: 'สร้างบัญชี',
    sign_in_btn: 'เข้าสู่ระบบ',
    creating_account: 'กำลังสร้างบัญชี…',
    signing_in: 'กำลังเข้าสู่ระบบ…',
    have_account: 'มีบัญชีอยู่แล้ว?',
    new_to_site: 'ยังไม่มีบัญชี?',
    create_account_link: 'สร้างบัญชี',
    signups_closed: 'ขณะนี้ปิดรับสมัครสมาชิกใหม่',
    error_login_failed: 'เข้าสู่ระบบไม่สำเร็จ',
    error_register_failed: 'สมัครสมาชิกไม่สำเร็จ',
  },

  prompt_form: {
    back: 'ย้อนกลับ',
    error_title_required: 'กรุณากรอกชื่อเรื่อง',
    error_content_required: 'กรุณากรอกเนื้อหา Prompt',
    error_generic: 'เกิดข้อผิดพลาด',
    title_label: 'ชื่อเรื่อง',
    title_placeholder: 'เช่น Cinematic Portrait Generator',
    description_label: 'คำอธิบาย',
    optional: 'ไม่บังคับ',
    description_placeholder: 'คำอธิบายสั้นๆ เกี่ยวกับสิ่งที่ Prompt นี้ทำ',
    content_label: 'เนื้อหา Prompt',
    content_chars: '{count} ตัวอักษร',
    content_placeholder: 'เขียน Prompt ของคุณที่นี่…\n\nเคล็ดลับ: เขียนเหมือนบรีฟ — บทบาท งาน ข้อจำกัด ตัวอย่าง',
    category_label: 'หมวดหมู่',
    images_label: 'รูปภาพ',
    images_helper: 'รูปแรก = ภาพปก เว้นว่างได้เพื่อใช้พรีวิวข้อความที่สร้างอัตโนมัติ',
    models_label: 'AI Model',
    models_optional_max: 'ไม่บังคับ · สูงสุด 10',
    tags_label: 'แท็ก',
    tags_placeholder: 'พิมพ์แล้วกด Enter หรือคอมมา…',
    visibility_label: 'การมองเห็น',
    visibility_public: 'สาธารณะ',
    visibility_public_desc: 'แสดงในแกลเลอรี',
    visibility_private: 'ส่วนตัว',
    visibility_private_desc: 'มีเพียงคุณเท่านั้นที่เห็น',
    location_label: 'สถานที่ (ไม่บังคับ)',
    location_placeholder: 'ค้นหาสถานที่ — เมือง สถานที่สำคัญ หรือที่อยู่',
    location_hint: 'ปักหมุด Prompt นี้ที่จุดเฉพาะบนแผนที่โลก เว้นว่างเพื่อใช้ตำแหน่งจากโปรไฟล์ของคุณ',
    location_clear: 'ล้าง',
    cancel: 'ยกเลิก',
    saving: 'กำลังบันทึก…',
    live_preview: 'พรีวิวสด',
    preview_subtitle: 'Prompt ของคุณจะปรากฏในแกลเลอรีแบบนี้',
    preview_footer: 'จำนวนการบันทึกและรูปโปรไฟล์ผู้เขียนจะอัปเดตในรายการจริง',
  },

  profile: {
    eyebrow_curator: 'ผู้สร้าง',
    eyebrow_own: 'โปรไฟล์ของคุณ',
    eyebrow_system_bot: 'บอทของระบบ',
    joined: 'เข้าร่วมเมื่อ {date}',
    back: 'ย้อนกลับ',
    edit_profile: 'แก้ไขโปรไฟล์',
    new_prompt: 'สร้าง Prompt',
    follow: 'ติดตาม',
    following: 'กำลังติดตาม',
    unfollow: 'เลิกติดตาม',
    unfollowing: 'กำลังเลิกติดตาม…',
    following_progress: 'กำลังติดตาม…',
    follow_error: 'อัปเดตการติดตามไม่สำเร็จ',
    stat_followers: 'ผู้ติดตาม',
    stat_following: 'กำลังติดตาม',
    stat_prompts: 'Prompt',
    stat_total_saves: 'ยอดบันทึก',
    stat_total_copies: 'ยอดคัดลอก',
    stat_total_views: 'ยอดการเข้าชม',
    your_library: 'คลังของคุณ',
    curated: 'คัดสรร',
    your_collections: 'คอลเลกชันของคุณ',
    collections: 'คอลเลกชัน',
    manage: 'จัดการ →',
    private: 'ส่วนตัว',
    section_eyebrow: 'คอลเลกชัน',
    empty_no_public: 'ยังไม่มี Prompt สาธารณะ',
    your_published_prompts: 'Prompt ที่คุณเผยแพร่',
    published_by: 'เผยแพร่โดย {name}',
    type_label: 'ประเภท',
    tag_label: 'แท็ก',
    all: 'ทั้งหมด',
    clear_filters: 'ล้างตัวกรอง',
    showing: 'แสดง {count} จาก {total}',
    tag_title_one: 'มี Prompt {count} รายการที่ติดแท็ก #{name}',
    tag_title_other: 'มี Prompt {count} รายการที่ติดแท็ก #{name}',
    empty_filters: 'ไม่พบ Prompt ที่ตรงกับตัวกรองนี้',
    empty_publish_first: 'เผยแพร่ Prompt แรกของคุณแล้วจะปรากฏที่นี่',
    empty_user_no_public: '{name} ยังไม่ได้เผยแพร่ Prompt สาธารณะ',
    user_not_found: 'ไม่พบผู้ใช้',
    user_not_found_body: 'เราไม่พบผู้ใช้ที่มีชื่อนี้',
    back_to_gallery: '← กลับสู่แกลเลอรี',
    private_collection_count_one: '{count} Prompt',
    private_collection_count_other: '{count} Prompt',
  },

  prompt_detail: {
    copy_prompt: 'คัดลอก Prompt',
    copy: 'คัดลอก',
    copied: 'คัดลอกแล้ว!',
    copied_short: 'คัดลอกแล้ว',
    copying: 'กำลังคัดลอก…',
    save: 'บันทึก',
    saved: 'บันทึกแล้ว',
    remix: 'ดัดแปลง',
    remix_of: 'ดัดแปลงจาก',
    by: 'โดย',
    remix_tooltip: 'ดัดแปลง Prompt นี้ — สร้างเวอร์ชันของคุณเอง',
    the_prompt: 'ตัว Prompt',
    customize: 'ปรับแต่ง',
    fill_blanks: 'เติมช่องว่าง',
    filled: 'ที่กรอกแล้ว',
    reset: 'รีเซ็ต',
    stat_saves: 'บันทึก',
    stat_copies: 'คัดลอก',
    stat_views: 'เข้าชม',
    back_to_gallery: 'กลับสู่แกลเลอรี',
    not_found: 'ไม่พบ Prompt นี้',
    works_with: 'ใช้ได้กับ',
    open_model_details: 'เปิดรายละเอียด {name}',
    sign_in_to_save: 'เข้าสู่ระบบเพื่อบันทึก',
    saved_to_collection: 'บันทึกลงคอลเลกชันของคุณแล้ว',
    removed_from_collection: 'นำออกจากคอลเลกชันของคุณแล้ว',
    copied_to_clipboard: 'คัดลอกไปยังคลิปบอร์ดแล้ว',
    copied_with_values: 'คัดลอกพร้อมค่าที่กรอก {count} ช่อง',
    clipboard_error: 'เข้าถึงคลิปบอร์ดไม่ได้',
    ready_to_copy: 'พร้อมคัดลอก',
    preview_with_values: 'พรีวิวพร้อมค่าของคุณ',
    click_to_fill: 'คลิกเพื่อกรอก {name}',
    tip_leave_blank_pre: 'เคล็ดลับ: เว้นว่างไว้เพื่อคงค่า',
    tip_leave_blank_post: 'เดิมในข้อความที่คัดลอก',
    lineage: 'สายเลือด',
    fork_tree: 'แผนผังการดัดแปลง',
    view_profile_of: 'ดูโปรไฟล์ของ {name}',
  },

  library: {
    not_found_title: 'ไม่พบหมวดหมู่',
    not_found_msg: 'ไม่พบหมวดหมู่นี้',
    browse_libraries: 'ดูหมวดหมู่ทั้งหมด',
    breadcrumb_home: 'หน้าแรก',
    breadcrumb_libraries: 'หมวดหมู่',
    stat_prompts: 'Prompt',
    stat_contributors: 'ผู้สร้าง',
    stat_saves_month: 'บันทึกเดือนนี้',
    follow_library: 'ติดตามหมวดนี้',
    share_a_prompt: 'แบ่งปัน Prompt หมวด {name}',
    all_topics: 'ทุกหัวข้อ',
    editors_pick: 'บรรณาธิการแนะนำ',
    open_prompt: 'เปิด Prompt',
    more_in: 'เพิ่มเติมในหมวด {name}',
    the_collection: 'คอลเลกชัน',
    sort_top: 'ยอดนิยม',
    sort_new: 'ใหม่สุด',
    sort_most_remixed: 'ดัดแปลงมากที่สุด',
    empty_tag: 'ยังไม่มี Prompt ที่ตรงกับแท็กนี้',
    empty_more: 'ยังไม่มี Prompt เพิ่มเติม',
    section_people: 'บุคคล',
    top_contributors: 'ผู้สร้างยอดนิยม',
    followers_suffix: 'ผู้ติดตาม',
    saved_suffix: 'บันทึก',
  },

  model_detail: {
    not_found_title: 'ไม่พบโมเดล',
    not_found_body_pre: 'ไม่พบโมเดล AI ชื่อ',
    not_found_body_post: 'ในรายการ',
    back_to_models: 'กลับไปยังโมเดลทั้งหมด',
    browse_prompts: 'ดู Prompt',
    loading: 'กำลังโหลด…',
    all_models: 'โมเดลทั้งหมด',
    eyebrow: 'Prompt',
    designed_for: 'ออกแบบสำหรับ {name}',
    sort_featured: 'แนะนำ',
    sort_newest: 'ใหม่สุด',
    sort_top: 'ยอดนิยม',
    stat_prompts: 'Prompt',
    stat_hint: 'ที่ใช้โมเดลนี้',
    no_prompts_title: 'ยังไม่มี Prompt สำหรับ {name}',
    no_prompts_body: 'เป็นคนแรก — แชร์ Prompt ที่ออกแบบสำหรับโมเดลนี้',
    share_a_prompt: 'แบ่งปัน Prompt',
    empty_message: 'ยังไม่มี Prompt ที่ใช้ {name}',
    by_brand: 'โดย',
  },

  explore: {
    eyebrow: 'สำรวจคลัง',
    title_before: 'ค้นหา Prompt',
    title_em: 'ตามสาขา',
    title_after: '.',
    tagline: 'หมวดหมู่ที่คัดสรรโดยผู้ปฏิบัติงานจริง แต่ละหมวดเป็นคอลเลกชันที่มีชีวิต ไม่ใช่ที่ทิ้งของ',
    browse: 'เปิดดู',
    rising_eyebrow: 'สัปดาห์นี้',
    rising_title: 'Prompt มาแรง',
    view_all: 'ดูทั้งหมด',
  },

  tags: {
    back_to_gallery: 'กลับสู่แกลเลอรี',
    eyebrow: 'แท็กทั้งหมดในแกลเลอรี',
    title_before: 'คำศัพท์',
    title_em: 'ของ Prompt',
    title_after: '.',
    loading_directory: 'กำลังโหลดรายการ…',
    summary_top_tag: 'แท็กยอดนิยมมี',
    summary_tail: 'คำย่อที่ผู้สร้างใช้จัดระเบียบงานของตน — เลือกหนึ่งเพื่อดู Prompt ที่ตรงกัน',
    summary_tags_label: 'แท็ก',
    summary_prompts_label: 'Prompt',
    search_placeholder_loading: 'กำลังโหลด…',
    search_placeholder: 'ค้นหา {count} แท็ก…',
    clear_filter: 'ล้างตัวกรอง',
    editors_pick: 'บรรณาธิการแนะนำ',
    most_used: 'ใช้มากที่สุด',
    directory: 'รายการ',
    matching: 'ที่ตรงกับ "{query}"',
    all_tags: 'แท็กทั้งหมด',
    sort_featured: 'แนะนำ',
    sort_by_count: 'ตามจำนวน',
    sort_alpha: 'A–Z',
    no_match_title: 'ไม่พบแท็กที่ตรงกับ "{query}"',
    no_match_body: 'ลองคำอื่น หรือล้างตัวกรองเพื่อดูแท็กทั้งหมด {total} แท็ก',
  },

  collections: {
    eyebrow: 'คอลเลกชัน',
    // Renders as: "[เพลย์ลิสต์] Prompt ที่คัดสรรมาแล้ว"  (italic on title_em)
    // Reads more naturally in Thai than mirroring EN word-by-word.
    title_before: '',
    title_em: 'เพลย์ลิสต์',
    title_after: 'Prompt ที่คัดสรรมาแล้ว',
    tagline: 'คอลเลกชันสาธารณะจากทั่ว Prompt Save — แพ็กตามธีม ชุดเครื่องมือทำงาน และที่บรรณาธิการแนะนำ',
    sort_newest: 'ใหม่สุด',
    sort_largest: 'ใหญ่สุด',
    search_placeholder: 'ค้นหาตามชื่อหรือคำอธิบาย…',
    clear_search: 'ล้างการค้นหา',
    empty_match_title: 'ไม่พบคอลเลกชันที่ตรงกับการค้นหา',
    empty_no_public_title: 'ยังไม่มีคอลเลกชันสาธารณะ',
    empty_match_body: 'ลองคำอื่น หรือล้างการค้นหา',
    empty_no_public_body: 'เป็นคนแรก — สร้างคอลเลกชันและตั้งเป็นสาธารณะ',
    load_more: 'โหลดเพิ่ม',
    showing_of: 'แสดง {shown} จาก {total}',
  },

  cookie: {
    title: 'ตัวเลือกความเป็นส่วนตัวของคุณ',
    intro:
      'เราใช้คุกกี้เพื่อให้เว็บทำงานได้ จดจำการตั้งค่าของคุณ และ (เมื่อคุณยินยอม) วัดสถิติการเข้าชม รวมถึงแสดงโฆษณาที่เกี่ยวข้อง คุณเปลี่ยนตัวเลือกได้ตลอดเวลาจาก Footer',
    necessary_label: 'จำเป็น',
    necessary_desc: 'จำเป็นต่อการเข้าสู่ระบบ ความปลอดภัย และฟีเจอร์หลัก เปิดอยู่ตลอดเวลา',
    analytics_label: 'วิเคราะห์การใช้งาน',
    analytics_desc: 'สถิติการใช้งานแบบไม่ระบุตัวตน เพื่อให้เราเห็นจุดที่ควรปรับปรุง',
    advertising_label: 'โฆษณา',
    advertising_desc: 'ใช้โดย Google AdSense เพื่อแสดงโฆษณาที่ตรงความสนใจและไม่ซ้ำซาก',
    accept_all: 'ยอมรับทั้งหมด',
    reject_non_essential: 'ปฏิเสธทั้งหมดที่ไม่จำเป็น',
    save_preferences: 'บันทึกการตั้งค่า',
    learn_more: 'อ่านนโยบายคุกกี้',
    settings_button: 'ตั้งค่าคุกกี้',
    always_on: 'เปิดตลอดเวลา',
  },

  footer: {
    tagline: 'แกลเลอรีของ Prompt ดี ๆ',
    section_product: 'ผลิตภัณฑ์',
    section_legal: 'ข้อกฎหมาย',
    section_resources: 'แหล่งข้อมูล',
    link_explore: 'สำรวจ',
    link_trending: 'มาแรง',
    link_collections: 'คอลเลกชัน',
    link_tags: 'แท็ก',
    link_about: 'เกี่ยวกับ',
    link_privacy: 'นโยบายความเป็นส่วนตัว',
    link_terms: 'เงื่อนไขการใช้งาน',
    link_cookies: 'นโยบายคุกกี้',
    link_guidelines: 'แนวทางชุมชน',
    link_dmca: 'DMCA',
    link_disclaimer: 'ข้อปฏิเสธความรับผิด',
    link_security: 'ความปลอดภัย',
    copyright: '© {year} {site} สงวนลิขสิทธิ์',
    made_with: 'สำหรับครีเอเตอร์ นักเขียน และวิศวกรซอฟต์แวร์',
  },

  legal: {
    last_updated: 'อัปเดตล่าสุด: {date}',
    back_home: 'กลับสู่หน้าแรก',
    contents: 'ในหน้านี้',
  },

  errors: {
    not_found_title: 'ไม่พบหน้านี้',
    not_found_body: 'เราหาหน้าที่คุณต้องการไม่เจอ — อาจถูกย้ายหรือลบออกแล้ว',
    server_title: 'เกิดข้อผิดพลาด',
    server_body: 'มีบางอย่างผิดพลาดขณะโหลดหน้านี้ ลองอีกครั้ง หรือกลับสู่หน้าแรก',
    try_again: 'ลองอีกครั้ง',
  },

  // ── Admin console ───────────────────────────────────────────────────
  admin_common: {
    loading: 'กำลังโหลด…',
    saving: 'กำลังบันทึก…',
    save: 'บันทึก',
    save_changes: 'บันทึกการเปลี่ยนแปลง',
    cancel: 'ยกเลิก',
    delete: 'ลบ',
    edit: 'แก้ไข',
    create: 'สร้าง',
    close: 'ปิด',
    search: 'ค้นหา',
    reset: 'รีเซ็ต',
    export: 'ส่งออก',
    download: 'ดาวน์โหลด',
    actions: 'การกระทำ',
    status: 'สถานะ',
    when: 'เมื่อ',
    target: 'เป้าหมาย',
    name: 'ชื่อ',
    role: 'บทบาท',
    created: 'สร้างเมื่อ',
    last_login: 'เข้าสู่ระบบล่าสุด',
    page_of: 'หน้า {page} จาก {total}',
    previous: 'ก่อนหน้า',
    next: 'ถัดไป',
    prev_arrow: '← ก่อนหน้า',
    next_arrow: 'ถัดไป →',
    all: 'ทั้งหมด',
    none: '—',
    yes: 'ใช่',
    no: 'ไม่',
    on: 'เปิด',
    off: 'ปิด',
    enabled: 'เปิดใช้งาน',
    disabled: 'ปิดใช้งาน',
    active: 'ใช้งาน',
    inactive: 'ไม่ใช้งาน',
    hidden: 'ซ่อน',
    published: 'เผยแพร่',
    private: 'ส่วนตัว',
    optional: 'ไม่บังคับ',
    failed_generic: 'ไม่สำเร็จ',
    save_failed: 'บันทึกไม่สำเร็จ',
    couldnt_load: 'โหลดไม่สำเร็จ',
    couldnt_delete: 'ลบไม่สำเร็จ',
    couldnt_update: 'อัปเดตไม่สำเร็จ',
    moderator_readonly: 'บทบาท Moderator ดูได้แต่แก้ไข {resource} ไม่ได้ ติดต่อ admin หรือ super_admin เพื่อแก้ไข',
  },

  admin_root: {
    eyebrow: 'คอนโซล',
    title: 'ผู้ดูแลระบบ',
    signed_in_as: 'เข้าสู่ระบบในชื่อ',
    role_super_admin: 'Super admin',
    role_admin: 'Admin',
    role_moderator: 'Moderator',
    export_report: 'ส่งออกรายงาน',
    manage_admins: 'จัดการผู้ดูแล',
    sign_out: 'ออกจากระบบ',
    sign_out_title: 'ออกจากคอนโซลผู้ดูแลเท่านั้น — เซสชัน Prompt Save ปกติของคุณไม่ได้รับผลกระทบ',
    report_exported: 'ส่งออกรายงานแล้ว',
    couldnt_export: 'ส่งออกไม่สำเร็จ',
    metric_total_users: 'ผู้ใช้ทั้งหมด',
    metric_total_admins: 'ผู้ดูแลทั้งหมด',
    metric_total_prompts: 'Prompt ทั้งหมด',
    metric_public_prompts: 'Prompt สาธารณะ',
    metric_private_prompts: 'Prompt ส่วนตัว',
    metric_total_saves: 'ยอดบันทึกรวม',
  },

  admin_sidebar: {
    menu: 'เมนู',
    open_nav_aria: 'เปิดเมนูผู้ดูแล',
    sections_aria: 'หมวดผู้ดูแล',
    group_overview: 'ภาพรวม',
    group_content: 'เนื้อหา',
    group_users: 'ผู้ใช้',
    group_moderation: 'การกำกับดูแล',
    group_system: 'ระบบ',
    item_dashboard: 'แดชบอร์ด',
    item_online: 'ออนไลน์',
    item_prompts: 'Prompt',
    item_libraries: 'หมวดหมู่',
    item_aimodels: 'AI Models',
    item_announcements: 'ประกาศ',
    item_users: 'ผู้ใช้เว็บ',
    item_admins: 'ผู้ดูแล',
    item_reports: 'รายงาน',
    item_comments: 'คอมเมนต์',
    item_abuse: 'เหตุการณ์ที่น่าสงสัย',
    item_audit: 'บันทึกการกระทำ (Audit log)',
    item_config: 'ตั้งค่า',
    item_email: 'อีเมล',
    item_database: 'ฐานข้อมูล',
    item_consent: 'บันทึกความยินยอม',
    item_retention: 'การเก็บข้อมูล',
    item_seed_pool: 'คลังต้นแบบ (Seed pool)',
    item_subscriptions: 'แพ็กเกจสมาชิก',
    badge_pending_aria: 'รอดำเนินการ {count} รายการ',
  },

  admin_login: {
    badge: 'พื้นที่ผู้ดูแล',
    title: 'เข้าสู่ระบบผู้ดูแล',
    subtitle: 'การเข้าสู่ระบบนี้แยกจากบัญชี Prompt Save ปกติ เฉพาะบัญชีผู้ดูแลที่ได้รับสิทธิ์เท่านั้นที่ลงชื่อเข้าใช้ที่นี่ได้',
    cross_session_pre: 'คุณกำลังเข้าสู่ระบบในชื่อ',
    cross_session_post: 'บนเว็บปกติ ผู้ดูแลเป็นระบบแยก — รหัสผ่านบนเว็บปกติจะใช้ที่นี่ไม่ได้ ถ้าคุณไม่มีบัญชีผู้ดูแล',
    return_to_site: 'กลับสู่เว็บไซต์',
    or_word: 'หรือ',
    sign_out_site: 'ออกจากระบบเว็บปกติ',
    if_you_dont_need: 'ถ้าไม่ต้องการเซสชันทั้งสอง',
    field_identifier: 'ชื่อผู้ใช้หรืออีเมล',
    field_password: 'รหัสผ่าน',
    identifier_placeholder: 'admin_username หรือ admin@example.com',
    sign_in: 'เข้าสู่ระบบ',
    signing_in: 'กำลังเข้าสู่ระบบ…',
    back_to_site: '← กลับสู่เว็บไซต์',
    logged_notice: 'การพยายามเข้าสู่ระบบทุกครั้งจะถูกบันทึกไว้',
    error_login_failed: 'เข้าสู่ระบบไม่สำเร็จ',
  },

  admin_dashboard: {
    metric_total_prompts: 'Prompt ทั้งหมด',
    metric_total_users: 'ผู้ใช้ทั้งหมด',
    metric_private_drafts: 'ฉบับร่างส่วนตัว',
    metric_avg_saves: 'ยอดบันทึกเฉลี่ย / Prompt',
    hint_last_days: '{days} วันล่าสุด',
    hint_members: 'สมาชิก',
    hint_not_published: 'ยังไม่เผยแพร่',
    hint_all_time: 'ตลอดเวลา',
    hint_n_admins_one: 'ผู้ดูแล {count} คน',
    hint_n_admins_other: 'ผู้ดูแล {count} คน',
    hint_private_hidden: 'ซ่อนจากการค้นหา {count} รายการ',
    submissions_title: 'การส่ง Prompt',
    submissions_sub: '{days} วันล่าสุด · รายวัน',
    spark_published: 'เผยแพร่',
    spark_private: 'ส่วนตัว',
    spark_saves: 'บันทึก',
    top_categories: 'หมวดยอดนิยม',
    top_categories_sub: 'ตามจำนวนที่เผยแพร่',
    no_categories: 'ยังไม่มีหมวดหมู่',
    recent_activity: 'กิจกรรมล่าสุด',
    recent_activity_sub: 'ทั่วทั้งระบบ · {count} รายการล่าสุด',
    no_activity: 'ยังไม่มีกิจกรรม',
    couldnt_load: 'โหลดข้อมูลแดชบอร์ดไม่สำเร็จ',
  },

  admin_prompts: {
    filter_all: 'ทั้งหมด',
    filter_published: 'เผยแพร่',
    filter_private: 'ส่วนตัว',
    search_placeholder: 'ค้นหา Prompt ตามชื่อหรือเนื้อหา',
    export: 'ส่งออก',
    generate: 'สร้างอัตโนมัติ',
    generate_title: 'สร้าง Prompt ที่เผยแพร่ได้อัตโนมัติ โดยระบุผู้แต่ง',
    n_selected: 'เลือก {count} รายการ',
    bulk_clear: 'ล้าง',
    bulk_hide: 'ซ่อน',
    bulk_unhide: 'เลิกซ่อน',
    bulk_queue: 'ย้ายเข้าคิว',
    bulk_delete: 'ลบ',
    bulk_delete_title: 'การลบหลายรายการจำกัดเฉพาะ super_admin',
    col_prompt: 'Prompt',
    col_author: 'ผู้แต่ง',
    col_category: 'หมวดหมู่',
    col_saves: 'บันทึก',
    col_views: 'เข้าชม',
    col_status: 'สถานะ',
    no_match: 'ไม่พบ Prompt ที่ตรงกัน',
    status_hidden: 'ซ่อน',
    status_hidden_title: 'ผู้ดูแลซ่อนไว้ — ไม่แสดงในมุมมองสาธารณะ',
    status_published: 'เผยแพร่',
    status_private: 'ส่วนตัว',
    type_remix: 'Remix',
    type_image_cover: 'ภาพปก',
    type_text_prompt: 'Prompt ข้อความ',
    cell_other: 'อื่นๆ',
    title_unhide: 'เลิกซ่อน — แสดงให้ทุกคนเห็น',
    title_hide: 'ซ่อนจากสาธารณะ — ยังคงความเป็นเจ้าของ',
    title_delete: 'ลบ Prompt ถาวร',
    total_prompts: 'Prompt ทั้งหมด {count} รายการ',
    confirm_delete_title: 'ลบ "{title}"?',
    confirm_delete_body: 'การกระทำนี้ย้อนกลับไม่ได้',
    confirm_bulk_delete_title: 'ลบ Prompt {count} รายการ (soft-delete)?',
    confirm_bulk_delete_body: 'กู้คืนได้จาก /admin#prompts (กรองส่วนตัว)',
    select_all_aria: 'เลือกที่มองเห็นทั้งหมด',
    prompt_deleted: 'ลบ Prompt แล้ว',
    couldnt_delete: 'ลบไม่สำเร็จ',
    couldnt_update_visibility: 'อัปเดตการมองเห็นไม่สำเร็จ',
    hidden_toast: 'ซ่อน "{title}" จากเว็บสาธารณะแล้ว',
    restored_toast: 'คืนค่า "{title}" สู่สาธารณะแล้ว',
    exported_n: 'ส่งออกแล้ว {count} Prompt',
    generated_n: 'สร้างแล้ว {count} Prompt',
    bulk_hidden: 'ซ่อนแล้ว {affected} จาก {requested}',
    bulk_unhidden: 'เลิกซ่อนแล้ว {affected} จาก {requested}',
    bulk_queued: 'เข้าคิวแล้ว {affected} จาก {requested}',
    bulk_deleted: 'ลบแล้ว {affected} จาก {requested}',
    hide_reason_prompt: 'เหตุผลซ่อน (spam / abuse / copyright / nsfw / other):',
  },

  admin_users: {
    search_placeholder: 'ค้นหาผู้ใช้ตามชื่อ ชื่อผู้ใช้ หรืออีเมล',
    admins_note_pre: 'ผู้ดูแลจัดการอยู่ในแท็บ',
    admins_note_tab: 'Admins',
    admins_note_post: '— ผู้ใช้และผู้ดูแลแยกกัน',
    col_user: 'ผู้ใช้',
    col_prompts: 'Prompt',
    col_saved: 'บันทึก',
    col_provider: 'ผู้ให้บริการ',
    col_joined: 'เข้าร่วม',
    no_match: 'ไม่พบผู้ใช้ที่ตรงกัน',
    suspend_placeholder: 'ระงับ…',
    suspend_title: 'ระงับผู้ใช้นี้ — เลือกระยะเวลา',
    unsuspend: 'ยกเลิกระงับ',
    unsuspend_title: 'ยกเลิกการระงับ',
    delete_user_title: 'ลบผู้ใช้',
    permaban: 'ระงับถาวร',
    suspended: 'ถูกระงับ',
    total_users: 'ผู้ใช้ทั้งหมด {count} ราย',
    confirm_delete_title: 'ลบบัญชี @{username}?',
    confirm_delete_body: 'การกระทำนี้จะลบ Prompt ของพวกเขาด้วยถาวร',
    confirm_suspend_title: 'ระงับ @{username} {label}?',
    confirm_suspend_body: 'พวกเขายังเข้าสู่ระบบและดูได้ แต่ทุกการเขียนจะถูกปฏิเสธด้วย 403',
    suspend_label_perma: 'ถาวร',
    suspend_label_for: 'เป็นเวลา {label}',
    suspend_reason_prompt: 'เหตุผล (แสดงเฉพาะผู้ดูแล ไม่แสดงผู้ใช้):',
    duration_1h: '1 ชั่วโมง',
    duration_24h: '24 ชั่วโมง',
    duration_7d: '7 วัน',
    duration_30d: '30 วัน',
    duration_perma: 'ถาวร',
    user_deleted: 'ลบผู้ใช้แล้ว',
    couldnt_delete_user: 'ลบผู้ใช้ไม่สำเร็จ',
    suspended_until: 'ระงับถึง {when}',
    suspended_perma: 'ระงับถาวร',
    couldnt_suspend: 'ระงับไม่สำเร็จ',
    couldnt_unsuspend: 'ยกเลิกระงับไม่สำเร็จ',
    unsuspended_toast: 'ยกเลิกระงับ @{username} แล้ว',
    rem_minutes: 'เหลือ {count} นาที',
    rem_hours: 'เหลือ {count} ชั่วโมง',
    rem_days: 'เหลือ {count} วัน',
    rem_expiring: 'กำลังหมดอายุ…',
  },

  admin_admins: {
    search_placeholder: 'ค้นหาผู้ดูแล',
    new_admin: 'เพิ่มผู้ดูแล',
    only_super_note: 'เฉพาะบัญชี',
    super_admin: 'super_admin',
    only_super_note_post: 'เท่านั้นที่สร้าง แก้ไข หรือลบผู้ดูแลได้ คุณยังดูรายการได้',
    col_admin: 'ผู้ดูแล',
    no_admins: 'ไม่พบผู้ดูแล',
    total_count_one: 'ผู้ดูแล {count} คน',
    total_count_other: 'ผู้ดูแล {count} คน',
    title_deactivate: 'ปิดใช้งาน',
    title_reactivate: 'เปิดใช้งานอีกครั้ง',
    title_edit: 'แก้ไขผู้ดูแล',
    title_delete: 'ลบผู้ดูแล',
    confirm_delete_title: 'ลบผู้ดูแล @{username}?',
    confirm_delete_body: 'การกระทำนี้ไม่กระทบผู้ใช้เว็บปกติ',
    cant_delete_self: 'คุณลบบัญชีผู้ดูแลของตัวเองไม่ได้',
    cant_deactivate_self: 'คุณปิดใช้งานบัญชีของตัวเองไม่ได้',
    removed_toast: 'ลบ {name} แล้ว',
    reactivated_toast: 'เปิดใช้งาน {name} อีกครั้งแล้ว',
    deactivated_toast: 'ปิดใช้งาน {name} แล้ว',
    couldnt_load: 'โหลดผู้ดูแลไม่สำเร็จ',
    couldnt_delete: 'ลบไม่สำเร็จ',
    couldnt_update: 'อัปเดตไม่สำเร็จ',
    dlg_new: 'ผู้ดูแลใหม่',
    dlg_edit: 'แก้ไขผู้ดูแล',
    dlg_create_title: 'สร้างบัญชีผู้ดูแล',
    dlg_edit_title: 'แก้ไข {name}',
    dlg_field_username: 'ชื่อผู้ใช้',
    dlg_field_display_name: 'ชื่อที่แสดง',
    dlg_field_email: 'อีเมล (ไม่บังคับ)',
    dlg_field_password_new: 'รหัสผ่าน (10+ ตัวอักษร)',
    dlg_field_password_edit: 'รหัสผ่านใหม่ (เว้นว่างเพื่อคงเดิม)',
    dlg_field_role: 'บทบาท',
    dlg_field_status: 'สถานะ',
    dlg_role_moderator: 'Moderator — อ่านและซ่อน Prompt',
    dlg_role_admin: 'Admin — กำกับดูแล + แดชบอร์ด',
    dlg_role_super_admin: 'Super admin — ทุกอย่าง (รวมจัดการผู้ดูแล)',
    dlg_status_active: 'ใช้งานได้ (เข้าสู่ระบบได้)',
    dlg_status_inactive: 'ปิดใช้งาน (เข้าสู่ระบบไม่ได้)',
    dlg_username_ph: 'เช่น alice_admin',
    dlg_name_ph: 'Alice Admin',
    dlg_email_ph: 'alice@example.com',
    dlg_password_ph_new: 'อย่างน้อย 10 ตัวอักษร',
    dlg_create_btn: 'สร้างผู้ดูแล',
    dlg_validation_required: 'ต้องระบุชื่อผู้ใช้ ชื่อ และรหัสผ่าน 10+ ตัวอักษร',
    admin_created: 'สร้างผู้ดูแลแล้ว',
    admin_updated: 'อัปเดตผู้ดูแลแล้ว',
    status_active: 'ใช้งาน',
    status_inactive: 'ไม่ใช้งาน',
  },

  admin_audit: {
    search_placeholder: 'ค้นหาตามผู้ดูแล เป้าหมาย หรือการกระทำ',
    filter_all_actions: 'การกระทำทั้งหมด',
    filter_last_24h: '24 ชั่วโมงล่าสุด',
    filter_last_7d: '7 วันล่าสุด',
    filter_last_30d: '30 วันล่าสุด',
    filter_last_90d: '90 วันล่าสุด',
    filter_all_time: 'ตลอดเวลา',
    n_entries_one: '{count} รายการ',
    n_entries_other: '{count} รายการ',
    col_admin: 'ผู้ดูแล',
    col_action: 'การกระทำ',
    col_target: 'เป้าหมาย',
    col_ip: 'IP',
    no_match: 'ไม่พบรายการ Audit ที่ตรงกัน',
    details: 'รายละเอียด',
  },

  admin_reports: {
    filter_open: 'รอดำเนินการ',
    filter_resolved: 'ดำเนินการแล้ว',
    filter_dismissed: 'ปฏิเสธ',
    col_reason: 'เหตุผล',
    col_target: 'เป้าหมาย',
    col_reporter: 'ผู้รายงาน',
    col_action: 'การกระทำ',
    no_match: 'ไม่พบรายงานที่ตรงกัน',
    btn_hide: 'ซ่อน',
    btn_hide_title: 'ดำเนินการ + ซ่อนเป้าหมาย',
    btn_resolve: 'ดำเนินการแล้ว',
    btn_resolve_title: 'ทำเครื่องหมายว่าดำเนินการแล้วโดยไม่ซ่อน',
    btn_dismiss: 'ปฏิเสธ',
    btn_dismiss_title: 'ไม่ดำเนินการ — รายงานเท็จ',
    resolved_by: 'ดำเนินการโดย @{username}: {note}',
  },

  admin_comments: {
    filter_live: 'แสดง',
    filter_hidden: 'ซ่อนอยู่',
    filter_deleted: 'ลบแล้ว',
    search_placeholder: 'ค้นหาในข้อความคอมเมนต์',
    n_comments_one: '{count} คอมเมนต์',
    n_comments_other: '{count} คอมเมนต์',
    col_comment: 'คอมเมนต์',
    col_on_prompt: 'บน Prompt',
    no_match: 'ไม่พบคอมเมนต์ที่ตรงกัน',
    deleted_by_user: 'ลบโดยผู้ใช้',
    deleted_prompt: '(Prompt ถูกลบแล้ว)',
    btn_unhide: 'เลิกซ่อน',
    btn_hide: 'ซ่อน',
    hard_delete_title: 'ลบถาวร',
    hide_reason_prompt: 'เหตุผลซ่อนคอมเมนต์นี้?\n{options}',
    confirm_hard_delete_title: 'ลบคอมเมนต์นี้ถาวร?',
    confirm_hard_delete_body: '"{snippet}"\n\nย้อนกลับไม่ได้ — โดยปกติควรใช้ soft-hide',
    confirm_hard_delete_btn: 'ลบถาวร',
    comment_hidden: 'ซ่อนคอมเมนต์แล้ว',
    comment_restored: 'คืนค่าคอมเมนต์แล้ว',
    comment_deleted_perm: 'ลบคอมเมนต์ถาวรแล้ว',
  },

  admin_abuse: {
    filter_ip_placeholder: 'กรองตาม IP — เช่น 1.2.3.4',
    n_events_one: '{count} เหตุการณ์',
    n_events_other: '{count} เหตุการณ์',
    col_kind: 'ประเภท',
    col_label: 'ป้ายกำกับ',
    col_endpoint: 'Endpoint',
    no_match: 'ไม่พบสัญญาณการละเมิดที่ตรงกัน อาจไม่มีใครทำผิดหรือเงื่อนไขแคบเกินไป',
    details: 'รายละเอียด',
    title_filter_ip: 'กรองตาม IP นี้',
  },

  admin_config: {
    title: 'การตั้งค่าเว็บไซต์',
    subtitle: 'การตั้งค่าและข้อความที่แก้ไขได้แบบเรียลไทม์ คีย์สาธารณะแสดงที่',
    save_n: 'บันทึก ({count})',
    moderator_readonly_note: 'บัญชี Moderator ดูการตั้งค่าได้แต่แก้ไขไม่ได้ ติดต่อ admin หรือ super_admin เพื่อแก้ไข',
    not_writable_title: 'Moderator แก้ไขการตั้งค่าเว็บไซต์ไม่ได้',
    saved_n_one: 'บันทึกแล้ว {count} รายการ',
    saved_n_other: 'บันทึกแล้ว {count} รายการ',
    couldnt_load: 'โหลดการตั้งค่าไม่สำเร็จ',
    loading_settings: 'กำลังโหลดการตั้งค่า…',
  },

  admin_libraries: {
    title: 'หมวดหมู่ (Libraries)',
    subtitle: 'หน้าหมวดหมู่ที่จัดทำโดยบรรณาธิการ สลับการมองเห็น แก้ไขรูปลักษณ์ จัดลำดับ',
    new_library: 'สร้างหมวดใหม่',
    empty: 'ยังไม่มีหมวด คลิก {btn} เพื่อสร้าง',
    couldnt_load: 'โหลดหมวดไม่สำเร็จ',
    couldnt_delete: 'ลบไม่สำเร็จ',
    couldnt_update: 'อัปเดตหมวดไม่สำเร็จ',
    confirm_delete_title: 'ลบหมวด "{name}"?',
    confirm_delete_body: 'Prompt จะถูกเก็บไว้ ลบเฉพาะหน้าหมวดเท่านั้น',
    deleted_toast: 'ลบ {name} แล้ว',
    enabled_toast: 'เปิดใช้งาน {name} แล้ว',
    disabled_toast: 'ปิดใช้งาน {name} แล้ว',
    tile_enabled: 'เปิดใช้งาน',
    tile_hidden: 'ซ่อน',
    tile_edit: 'แก้ไข',
    tile_delete_title: 'ลบหมวด',
    dlg_new: 'ใหม่',
    dlg_edit: 'แก้ไข',
    dlg_create_title: 'สร้างหมวด',
    dlg_edit_title: 'แก้ไข {name}',
    dlg_field_name: 'ชื่อ',
    dlg_field_name_hint: 'เช่น Writing, Photography',
    dlg_field_slug: 'URL slug',
    dlg_field_slug_hint: 'a-z, 0-9, ขีดกลาง เว้นว่างเพื่อสร้างจากชื่ออัตโนมัติ',
    dlg_field_slug_ph: 'เช่น writing',
    dlg_field_category: 'หมวด Prompt',
    dlg_field_category_hint: 'ค่าของ Prompt.category ที่ใช้กรอง โดยปกติเหมือนชื่อ',
    dlg_field_category_ph: 'เช่น Writing',
    dlg_field_description: 'คำอธิบาย',
    dlg_field_description_hint: 'หนึ่งย่อหน้าสำหรับแบนเนอร์',
    dlg_field_hue: 'Hue (0-360)',
    dlg_field_hue_hint: 'สีของไล่ระดับแบนเนอร์',
    dlg_field_icon: 'ไอคอน',
    dlg_field_icon_hint: 'ชื่อ Material Symbols',
    dlg_field_icon_ph: 'เช่น edit_note',
    dlg_field_cover: 'URL ภาพปก',
    dlg_field_cover_hint: 'ไม่บังคับ — ทับไล่ระดับสีบนแบนเนอร์',
    dlg_field_sort: 'ลำดับการจัดเรียง',
    dlg_field_sort_hint: 'ตัวเลขน้อยจะแสดงก่อน',
    dlg_field_visibility: 'การมองเห็น',
    dlg_visibility_enabled: 'เปิดใช้งาน (สาธารณะ)',
    dlg_visibility_hidden: 'ซ่อน (เฉพาะผู้ดูแล)',
    dlg_create_btn: 'สร้างหมวด',
    dlg_validation: 'ต้องระบุชื่อและหมวด',
    library_created: 'สร้างหมวดแล้ว',
    library_updated: 'อัปเดตหมวดแล้ว',
  },

  admin_ai_models: {
    search_placeholder: 'ค้นหาตาม id ชื่อ หรือแบรนด์',
    filter_all_categories: 'ทุกหมวด',
    cat_text: 'ข้อความและแชท',
    cat_image: 'ภาพ',
    cat_video: 'วิดีโอ',
    cat_voice: 'เสียงและออดิโอ',
    cat_code: 'โค้ดและ Agent',
    showing_count: '{shown} จาก {total}',
    new_model: 'เพิ่ม AI Model',
    moderator_note: 'บทบาท Moderator ดูได้แต่แก้ไขไม่ได้ ติดต่อ admin หรือ super_admin เพื่อแก้ไข',
    col_model: 'โมเดล',
    col_brand: 'แบรนด์',
    col_category: 'หมวด',
    col_color: 'สี',
    col_order: 'ลำดับ',
    no_match: 'ไม่พบโมเดลที่ตรงกัน',
    status_enabled: 'เปิดใช้งาน',
    status_disabled: 'ปิดใช้งาน',
    title_disable: 'ปิดใช้งาน',
    title_enable: 'เปิดใช้งาน',
    title_edit: 'แก้ไข',
    title_delete: 'ลบ',
    couldnt_load: 'โหลด AI Models ไม่สำเร็จ',
    confirm_delete_title: 'ลบ "{name}" ({id})?',
    confirm_delete_body: 'Prompt ที่อ้างอิงโมเดลนี้ยังใช้ได้ — แต่จะไม่แสดง chip สวยจนกว่าจะเพิ่มโมเดลกลับหรือลบการอ้างอิง',
    removed_toast: 'ลบ {name} แล้ว',
    enabled_toast: 'เปิดใช้งาน {name} แล้ว',
    disabled_toast: 'ปิดใช้งาน {name} แล้ว',
    couldnt_delete: 'ลบไม่สำเร็จ',
    couldnt_update: 'อัปเดตไม่สำเร็จ',
    dlg_new: 'AI Model ใหม่',
    dlg_edit: 'แก้ไข AI Model',
    dlg_create_title: 'เพิ่มโมเดลใหม่',
    dlg_edit_title: 'แก้ไข {name}',
    dlg_preview_label: 'ตัวอย่าง:',
    dlg_preview_default: 'ชื่อโมเดล',
    dlg_field_id: 'ID (slug)',
    dlg_field_id_ph: 'เช่น openai/gpt-4o',
    dlg_field_id_locked_title: 'ID เปลี่ยนไม่ได้ — Prompt อ้างอิง slug นี้',
    dlg_field_id_hint_new: 'รูปแบบ: vendor/model-name พิมพ์เล็ก ไม่มีช่องว่าง เปลี่ยนภายหลังไม่ได้',
    dlg_field_id_hint_edit: 'Slug ล็อกหลังสร้าง — Prompt อ้างอิงใช้',
    dlg_field_name: 'ชื่อที่แสดง',
    dlg_field_name_ph: 'เช่น GPT-4o',
    dlg_field_brand: 'แบรนด์',
    dlg_field_brand_ph: 'เช่น OpenAI',
    dlg_field_category: 'หมวด',
    dlg_field_sort: 'ลำดับการจัดเรียง',
    dlg_field_color: 'สีแบรนด์ (hex)',
    dlg_field_icon: 'ไอคอน (ไม่บังคับ)',
    dlg_field_icon_ph: 'ชื่อ Material Symbols (เช่น smart_toy)',
    dlg_field_status: 'สถานะ',
    dlg_status_enabled: 'เปิดใช้งาน (เลือกได้บน /create)',
    dlg_status_disabled: 'ปิดใช้งาน (ซ่อนจากผู้แต่ง)',
    dlg_create_btn: 'สร้างโมเดล',
    dlg_validation_required: 'ต้องระบุชื่อและแบรนด์',
    dlg_validation_id: 'ID ต้องอยู่ในรูปแบบ vendor/model-name (เช่น openai/gpt-4o)',
    dlg_validation_exists: 'มี Model id "{id}" อยู่แล้ว',
    model_created: 'สร้างโมเดลแล้ว',
    model_updated: 'อัปเดตโมเดลแล้ว',
  },

  admin_database: {
    super_only_title: 'เฉพาะ Super-admin',
    super_only_body: 'เครื่องมือฐานข้อมูลแสดง schema ดิบและข้อมูลแถว เฉพาะ super_admin เท่านั้นที่เปิดได้',
    sub_tables: 'ตาราง',
    sub_diff: 'เปรียบเทียบ',
    sub_transfer: 'ถ่ายโอน',
    n_tables: '{count} ตาราง',
    n_columns_rows: '{cols} คอลัมน์ · {rows} แถว',
    redacted: 'ซ่อนไว้: {cols}',
    col_column: 'คอลัมน์',
    col_type: 'ชนิด',
    col_nullable: 'Null ได้',
    col_pk: 'PK',
    col_default: 'ค่าเริ่มต้น',
    sample_rows: 'ตัวอย่างแถว · หน้า {page}/{total}',
    no_rows: 'ไม่มีแถว',
    diff_title: 'เปรียบเทียบ Schema',
    diff_subtitle: 'เปรียบเทียบตารางและคอลัมน์ของ instance นี้กับ Prompt Save อีก instance ไม่คัดลอกข้อมูล — อ่านอย่างเดียว',
    field_remote_url: 'URL ฐาน API ของ remote',
    field_remote_url_ph: 'https://staging.example.com/api',
    field_remote_token: 'Admin JWT ที่ออกจาก remote',
    field_remote_token_super: 'Admin JWT ที่ออกจาก remote (super_admin)',
    field_remote_token_ph: 'eyJhbGciOiJIUzI1Ni…',
    btn_run_diff: 'เริ่มเปรียบเทียบ',
    btn_comparing: 'กำลังเปรียบเทียบ…',
    diff_validation: 'ต้องระบุทั้ง URL และ token ของ remote',
    diff_failed: 'เปรียบเทียบไม่สำเร็จ',
    result_title: 'ผลลัพธ์',
    metric_in_both: 'อยู่ทั้งสอง',
    metric_with_diffs: 'มีความต่าง',
    metric_only_here: 'มีเฉพาะที่นี่',
    metric_only_there: 'มีเฉพาะที่นั่น',
    only_here_count: 'มีเฉพาะที่นี่ ({count})',
    only_there_count: 'มีเฉพาะที่ remote ({count})',
    tables_in_both_count: 'ตารางที่อยู่ทั้งสอง ({count})',
    only_here_lc: 'มีเฉพาะที่นี่:',
    only_there_lc: 'มีเฉพาะที่นั่น:',
    type_mismatches_lc: 'ชนิดไม่ตรง:',
    rows_summary: 'แถว: {here} ที่นี่ · {there} ที่นั่น',
    transfer_title: 'ถ่ายโอนข้อมูลตาราง',
    transfer_subtitle_pre: 'ส่งทุกแถวของตารางท้องถิ่นไปยัง Prompt Save อีก instance',
    transfer_subtitle_warn: 'Dry-run ก่อนเสมอ',
    transfer_subtitle_post: 'ตาราง admins และ audit_logs ถูกบล็อก',
    field_table: 'ตาราง',
    field_table_ph: '— เลือกตาราง —',
    field_table_option: '{name} ({rows} แถว)',
    field_mode: 'โหมด',
    mode_replace: 'Replace — ลบแถวที่ remote แล้ว insert',
    mode_merge: 'Merge — upsert ตาม primary key',
    btn_dry_run: 'Dry run',
    btn_transfer: 'ถ่ายโอนจริง',
    transfer_confirm_title: 'ถ่ายโอน "{table}" ไป {url}?',
    transfer_confirm_body: 'โหมด: {mode}\n{warning}\n\nควร dry-run ก่อนถ้ายังไม่ได้ทำ ดำเนินการต่อ?',
    transfer_warn_replace: 'จะลบทุกแถวที่ remote ก่อน',
    transfer_warn_merge: 'แถวที่มีอยู่จะถูก upsert ตาม primary key',
    transfer_validation: 'ต้องระบุ URL token และตารางของ remote',
    transfer_failed: 'ถ่ายโอนไม่สำเร็จ',
    transferred_toast: 'ถ่ายโอน {count} แถวแล้ว',
    dry_run_preview: 'ตัวอย่าง Dry-run',
    transfer_complete: 'ถ่ายโอนเสร็จสิ้น',
    metric_local_rows: 'แถวท้องถิ่น',
    metric_remote_before: 'Remote ก่อน',
    metric_remote_after: 'Remote หลัง',
    metric_transferred: 'ถ่ายโอน',
    metric_skipped: 'ข้าม',
    n_errors_one: '{count} ข้อผิดพลาด:',
    n_errors_other: '{count} ข้อผิดพลาด:',
    couldnt_load_tables: 'โหลดตารางไม่สำเร็จ',
    couldnt_load_table: 'โหลดตารางไม่สำเร็จ',
    couldnt_load_rows: 'โหลดแถวไม่สำเร็จ',
  },

  admin_announcements: {
    summary: 'ใช้งาน {active} · ทั้งหมด {total}',
    new_announcement: 'ประกาศใหม่',
    col_banner: 'แบนเนอร์',
    col_severity: 'ระดับ',
    col_audience: 'กลุ่มเป้าหมาย',
    col_created: 'สร้างเมื่อ',
    by_username: 'โดย @{username}',
    empty: 'ยังไม่มีประกาศ คลิก {btn} เพื่อกระจายข่าวสาร',
    status_live: 'เผยแพร่',
    status_hidden: 'ซ่อน',
    title_hide: 'ซ่อน',
    title_activate: 'เปิดใช้งาน',
    title_edit: 'แก้ไข',
    title_delete: 'ลบ',
    confirm_delete_title: 'ลบ "{title}"?',
    confirm_delete_body: 'จะหายไปจากเว็บสาธารณะทันที',
    couldnt_load: 'โหลดประกาศไม่สำเร็จ',
    couldnt_update: 'อัปเดตไม่สำเร็จ',
    couldnt_delete: 'ลบไม่สำเร็จ',
    deleted_toast: 'ลบแล้ว',
    dlg_new: 'ประกาศใหม่',
    dlg_edit: 'แก้ไขประกาศ',
    dlg_create_title: 'กระจายแบนเนอร์',
    dlg_edit_title: 'แก้ไข "{title}"',
    dlg_field_title: 'หัวข้อ',
    dlg_field_title_ph: 'เช่น แจ้งบำรุงรักษาระบบ',
    dlg_field_body: 'เนื้อหา',
    dlg_field_body_ph: 'คำอธิบายสั้น เข้าใจง่าย',
    dlg_field_severity: 'ระดับ',
    dlg_field_audience: 'กลุ่มเป้าหมาย',
    severity_info: 'Info — เป็นกลาง ค่าเริ่มต้น',
    severity_success: 'Success — เขียว เฉลิมฉลอง',
    severity_warning: 'Warning — ส้ม ให้คำแนะนำ',
    severity_alert: 'Alert — แดง บล็อก',
    audience_everyone: 'ทุกคน',
    audience_authed: 'เฉพาะผู้ที่เข้าสู่ระบบ',
    audience_guests: 'เฉพาะผู้เยี่ยมชม',
    dlg_field_cta_label: 'CTA label (ไม่บังคับ)',
    dlg_field_cta_label_ph: 'อ่านเพิ่มเติม',
    dlg_field_cta_href: 'CTA URL (ไม่บังคับ)',
    dlg_field_cta_href_ph: '/changelog หรือ https://…',
    dlg_dismissible: 'ผู้ใช้ปิดเองได้',
    dlg_active: 'ใช้งาน (แสดงบนเว็บสาธารณะ)',
    dlg_create_btn: 'สร้างแบนเนอร์',
    dlg_preview_title: 'หัวข้อแบนเนอร์',
    dlg_preview_body: 'เนื้อหาแบนเนอร์ — สิ่งที่ผู้ใช้จะอ่าน',
    dlg_validation: 'ต้องระบุหัวข้อและเนื้อหา',
    announcement_created: 'สร้างประกาศแล้ว',
    announcement_updated: 'อัปเดตประกาศแล้ว',
  },

  admin_email: {
    sub_templates: 'เทมเพลต',
    sub_send: 'ส่ง',
    sub_log: 'บันทึก',
    n_templates: '{count} เทมเพลต',
    couldnt_load_templates: 'โหลดเทมเพลตไม่สำเร็จ',
    section_subject: 'หัวข้อ',
    section_body: 'เนื้อหา',
    section_variables: 'ตัวแปร',
    send_title: 'ส่งอีเมล',
    send_subtitle: 'Dry-run ก่อนเสมอ เซิร์ฟเวอร์บันทึกการส่งทุกครั้งในแท็บ {tab} และเขียน audit row',
    field_template: 'เทมเพลต',
    field_template_ph: '— เลือกเทมเพลต —',
    field_template_option: '{category} · {label}',
    field_vars: 'ตัวแปร (แทนที่ในเทมเพลต)',
    field_recipient: 'ผู้รับ',
    target_single: 'อีเมลเดียว',
    target_all: 'กระจายไปยังผู้ใช้ทุกคน',
    recipient_email_ph: 'someone@example.com',
    target_all_note: 'ส่งไปทุกผู้ใช้ที่มีอีเมล จำกัดไม่เกิน 500 ผู้รับฝั่งเซิร์ฟเวอร์',
    btn_dry_run: 'Dry run',
    btn_send: 'ส่ง',
    val_pick_template: 'เลือกเทมเพลต',
    val_recipient_required: 'ต้องระบุอีเมลผู้รับ',
    confirm_broadcast_title: 'กระจายไปยังผู้ใช้ทุกคน?',
    confirm_broadcast_body: 'อาจส่งอีเมลหลายร้อยฉบับ\n\nคลิก Cancel เพื่อ dry-run ก่อน',
    confirm_broadcast_btn: 'ส่งกระจาย',
    sent_summary: 'ส่ง {sent} · ล้มเหลว {failed}',
    sent_summary_skipped: 'ส่ง {sent} · ล้มเหลว {failed} · ข้าม {skipped}',
    send_failed: 'ส่งไม่สำเร็จ',
    dry_run_preview: 'ตัวอย่าง Dry-run',
    send_result: 'ผลการส่ง',
    metric_targets: 'เป้าหมาย',
    metric_sent: 'ส่งแล้ว',
    metric_failed: 'ล้มเหลว',
    metric_skipped: 'ข้าม',
    sample_subject: 'หัวข้อตัวอย่าง',
    sample_body: 'เนื้อหาตัวอย่าง',
    log_filter_all_statuses: 'ทุกสถานะ',
    log_status_sent: 'ส่งแล้ว',
    log_status_failed: 'ล้มเหลว',
    log_status_skipped: 'ข้าม (dev)',
    log_status_queued: 'อยู่ในคิว',
    log_filter_24h: '24 ชั่วโมงล่าสุด',
    log_filter_7d: '7 วันล่าสุด',
    log_filter_30d: '30 วันล่าสุด',
    log_filter_all: 'ตลอดเวลา',
    log_n_entries: '{count} รายการ',
    log_col_to: 'ถึง',
    log_col_subject: 'หัวข้อ',
    log_col_template: 'เทมเพลต',
    log_col_by: 'โดย',
    log_no_entries: 'ยังไม่มีอีเมลที่บันทึกไว้',
    log_system: 'ระบบ',
    couldnt_load_log: 'โหลดบันทึกไม่สำเร็จ',
  },

  admin_consent: {
    title: 'บันทึกความยินยอมคุกกี้',
    subtitle: 'บันทึก audit ที่เพิ่มเข้าได้อย่างเดียว สำหรับการตัดสินใจ Accept all / Reject / re-edit ทุกครั้ง ใช้แสดงต่อหน่วยงานกำกับ (PDPA ของไทย, GDPR Art. 7)',
    filter_user_id: 'กรองตาม user_id',
    filter_session_id: 'กรองตาม session_id (UUID นิรนาม)',
    export_csv: 'ส่งออกหน้านี้เป็น CSV',
    n_records: '{count} รายการ',
    col_recorded: 'บันทึกเมื่อ',
    col_user: 'ผู้ใช้',
    col_session: 'เซสชัน',
    col_categories: 'หมวด',
    col_version: 'เวอร์ชัน',
    col_ip: 'IP',
    no_records: 'ไม่มีรายการ',
    guest: 'ผู้เยี่ยมชม',
    failed_load: 'โหลดบันทึกความยินยอมไม่สำเร็จ',
  },

  admin_retention: {
    title: 'การเก็บรักษาข้อมูล',
    enabled_pre: 'เปิดใช้งาน — sweep พื้นหลังทำงานทุก',
    enabled_post: '',
    disabled: 'ปิด — sweep อัตโนมัติปิดอยู่ แก้ไขใน Config → Retention',
    tweak_note: 'ปรับเวลาต่อตารางใน Config → Retention; แท็บนี้แสดงสิ่งที่กำลังจะเกิด + การ sweep ครั้งล่าสุด',
    btn_run_now: 'เรียก sweep ตอนนี้',
    btn_running: 'กำลังทำงาน…',
    confirm_run_title: 'เรียก sweep ตอนนี้?',
    confirm_run_body: 'แถวที่เก่ากว่าช่วงเวลาที่กำหนดจะถูกลบถาวร',
    confirm_run_btn: 'เรียก sweep',
    deleted_summary: 'ลบ {count} แถว ใน {ms}ms',
    sweep_failed: 'Sweep ไม่สำเร็จ',
    col_table: 'ตาราง',
    col_keep_days: 'เก็บ (วัน)',
    col_total_rows: 'แถวทั้งหมด',
    col_would_delete: 'จะลบใน sweep ครั้งถัดไป',
    last_sweep: 'Sweep ครั้งล่าสุด',
    last_sweep_summary: '{when} — เสร็จใน {ms}ms ลบรวม: {count}',
    col_deleted: 'ลบแล้ว',
    col_note: 'หมายเหตุ',
  },

  admin_seed_pool: {
    chip_pool_size: 'ขนาดคลัง',
    chip_per_day: 'ต่อวัน',
    chip_runway: 'รันเวย์',
    chip_status: 'สถานะ',
    chip_runway_sub: 'วันที่อัตราปัจจุบัน',
    chip_status_auto: 'อัตโนมัติ',
    chip_status_paused: 'พัก',
    chip_status_sub: 'แก้ไขใน Config → Seed pool',
    chip_per_day_sub: '{count} ทุก {hours} ชม.',
    section_title: 'ปล่อย Prompt ที่สร้างไว้แบบหยด',
    section_sub: 'เติมคลังด้วยเครื่องมือ Generate (เปิด queue toggle); loop เบื้องหลังจะปล่อยตามตาราง Manual release คือคลิกเดียว — เหมาะหลังเติม เมื่อแกลเลอรีเงียบ หรือสำหรับทดสอบ',
    btn_bulk_fill: 'เติมหลายรายการ',
    btn_release_now: 'ปล่อย {count} ตอนนี้',
    btn_release_now_title: 'ปล่อย {count} ตอนนี้',
    btn_release_n: 'ปล่อย N…',
    next_up: 'ถัดไป (FIFO)',
    next_up_sub: 'รายการในคิวเก่ากว่าจะมาก่อน แก้ไข? ใช้แท็บ Prompts — แถวในคิวแสดงเหมือนกัน',
    col_title: 'ชื่อ',
    col_category: 'หมวด',
    col_author: 'ผู้แต่ง',
    col_queued: 'เข้าคิวเมื่อ',
    pool_empty: 'คลังว่างเปล่า คลิก {btn} เพื่อเพิ่ม Prompt',
    release_prompt: 'ปล่อยกี่รายการ? คลังมี {count}',
    release_summary: 'ปล่อยแล้ว {released} จาก {requested}',
    release_failed: 'ปล่อยไม่สำเร็จ',
    added_n: 'เพิ่ม {count} รายการเข้าคลัง',
    just_now: 'เมื่อสักครู่',
    rel_minutes: '{count} นาทีก่อน',
    rel_hours: '{count} ชั่วโมงก่อน',
    rel_days: '{count} วันก่อน',
  },

  admin_online: {
    online_window: 'ออนไลน์ ({min} นาทีล่าสุด)',
    last_hour: 'ชั่วโมงล่าสุด',
    last_24h: '24 ชั่วโมงล่าสุด',
    peak_window: 'สูงสุด ({hours} ชม.ล่าสุด)',
    density_title: 'ความหนาแน่นผู้ใช้',
    density_sub: 'ผู้ใช้ที่ไม่ซ้ำต่อช่วง {bucket} {unique} ผู้ใช้ที่ไม่ซ้ำในหน้าต่างนี้',
    density_sub_no_unique: 'ผู้ใช้ที่ไม่ซ้ำต่อช่วง {bucket}',
    window_2h: '2 ชั่วโมงล่าสุด',
    window_6h: '6 ชั่วโมงล่าสุด',
    window_24h: '24 ชั่วโมงล่าสุด',
    window_3d: '3 วันล่าสุด',
    window_7d: '7 วันล่าสุด',
    active_1m: 'ใช้งานใน 1 นาทีล่าสุด',
    active_5m: 'ใช้งานใน 5 นาทีล่าสุด',
    active_15m: 'ใช้งานใน 15 นาทีล่าสุด',
    active_1h: 'ใช้งานในชั่วโมงล่าสุด',
    active_24h: 'ใช้งานใน 24 ชั่วโมงล่าสุด',
    auto_refreshes: 'รีเฟรชอัตโนมัติทุก 10 วินาที',
    col_user: 'ผู้ใช้',
    col_last_seen: 'พบล่าสุด',
    no_active: 'ยังไม่มีใครออนไลน์ในช่วงนี้',
    no_density: 'ยังไม่มีกิจกรรม — แท่งจะเติมเมื่อมีผู้ใช้',
    rel_seconds: '{count} วินาทีก่อน',
    rel_minutes: '{count} นาทีก่อน',
    rel_hours: '{count} ชั่วโมงก่อน',
    rel_days: '{count} วันก่อน',
    active_now: 'ออนไลน์ตอนนี้',
    live: 'สด',
    auto_refresh_on: 'รีเฟรชอัตโนมัติเปิด',
    n_users_aria: '{when} — ผู้ใช้ {count} ราย',
  },

  admin_generate_dialog: {
    eyebrow: 'สร้างอัตโนมัติ',
    title: 'สร้าง Prompt',
    intro: 'สร้าง Prompt ที่เผยแพร่ได้จากคลังต้นแบบ โดยระบุผู้แต่ง ใช้ Preview เพื่อดูสิ่งที่จะสร้างก่อนยืนยัน',
    bot_label: 'โพสต์ในชื่อ @promptsave_bot (ระบบ)',
    bot_hint: 'โพสต์ของบอทมีป้าย BOT เพื่อให้ผู้อ่านรู้ว่าเป็นเนื้อหาระบบ ติ๊กออกถ้าต้องการระบุผู้ใช้จริง',
    author_label: 'ผู้แต่ง',
    author_hint: 'ผู้ใช้นี้จะแสดงเป็นผู้สร้าง Prompt',
    author_search_ph: 'ค้นหาตามชื่อ ชื่อผู้ใช้ หรืออีเมล',
    author_selected: 'เลือกแล้ว:',
    no_users_match: 'ไม่พบผู้ใช้ที่ตรงกัน',
    field_count: 'จำนวน',
    field_category: 'หมวด',
    field_destination: 'ปลายทาง',
    queue_toggle: 'เข้าคิวสำหรับปล่อยแบบหยด',
    queue_note_pre: 'อยู่ในคิว (ยังไม่เผยแพร่)',
    queue_note: 'ตัวปล่อยพื้นหลังจะปล่อยจากคลังตามจังหวะใน Config → Seed pool (ค่าเริ่มต้น: 1 Prompt ทุก 4 ชม. = ~6/วัน) ดูสถานะใน System → Seed pool',
    publish_now: 'เผยแพร่ทันที (เทียบกับบันทึกเป็นฉบับร่างส่วนตัวในโปรไฟล์ผู้แต่ง)',
    cat_all: 'ทั้งหมด (สุ่มผสม)',
    preview_label: 'ตัวอย่าง ({count})',
    btn_preview: 'ตัวอย่าง',
    btn_loading: 'กำลังโหลด…',
    btn_generate_n: 'สร้าง {count}',
    btn_saving: 'กำลังบันทึก…',
    pick_author: 'เลือกผู้แต่งก่อน',
    preview_failed: 'ดูตัวอย่างไม่สำเร็จ',
    save_failed: 'บันทึกไม่สำเร็จ',
  },

  pricing: {
    hero_title: 'ราคาตรงไปตรงมา',
    hero_tagline: 'เลือกแพ็กเกจที่เหมาะกับการทำงานของคุณ ยกเลิกเมื่อไหร่ก็ได้',
    cta_get_started: 'เริ่มใช้งาน',
    cta_current_plan: 'แพ็กเกจปัจจุบัน',
    cta_sign_in_first: 'เข้าสู่ระบบเพื่อสมัคร',
    disabled_message: 'ยังไม่เปิดให้สมัครสมาชิกในขณะนี้',
    payment_coming_soon: 'ระบบชำระเงินกำลังมา ติดต่อแอดมินเพื่อเปิดสิทธิ์ให้เองก่อนได้',
    interval_month: '/เดือน',
    interval_year: '/ปี',
    interval_lifetime: 'ตลอดชีพ',
    feature_label_max_prompts: 'จำนวน Prompt สูงสุด',
    feature_label_max_collections: 'จำนวนคอลเล็กชันสูงสุด',
    feature_label_priority_support: 'ซัพพอร์ตลำดับต้น',
    feature_label_api_access: 'เข้าถึง API',
    feature_label_no_ads: 'ไม่มีโฆษณา',
    feature_label_pro_badge: 'ป้าย Pro บนโปรไฟล์',
    feature_label_private_prompts: 'Prompt ส่วนตัวไม่จำกัด',
    feature_label_early_access: 'ลองดูฟีเจอร์ใหม่ก่อนใคร',
  },

  admin_subscriptions: {
    tab_plans: 'แพ็กเกจ',
    tab_subscribers: 'สมาชิก',
    new_plan_button: 'แพ็กเกจใหม่',
    no_plans: 'ยังไม่มีแพ็กเกจ — สร้างใหม่เพื่อเริ่มต้น',
    no_subscribers: 'ไม่พบสมาชิกตามตัวกรองนี้',
    n_subscribers: 'สมาชิก {count} คน',
    column_plan: 'แพ็กเกจ',
    column_price: 'ราคา',
    column_interval: 'รอบบิล',
    column_subscribers: 'สมาชิก',
    column_user: 'ผู้ใช้',
    column_started: 'เริ่ม',
    column_period_end: 'ต่ออายุ / สิ้นสุด',
    filter_active: 'ใช้งานอยู่',
    filter_cancelled: 'ยกเลิก',
    filter_expired: 'หมดอายุ',
    btn_cancel_sub: 'ยกเลิก',
    confirm_delete_plan_title: 'ลบแพ็กเกจ?',
    confirm_delete_plan: 'ลบแพ็กเกจ "{name}" อย่างถาวร? ระบบจะปฏิเสธหากยังมีคนสมัครอยู่',
    confirm_cancel_sub_title: 'ยกเลิกการสมัคร?',
    confirm_cancel_sub: 'ยกเลิกการสมัครของ {user}? ผู้ใช้ยังเข้าถึงได้จนหมดรอบ',
    plan_deleted: 'ลบแพ็กเกจแล้ว',
    plan_saved: 'บันทึกแพ็กเกจแล้ว',
    plan_created: 'สร้างแพ็กเกจแล้ว',
    sub_cancelled: 'ยกเลิกการสมัครแล้ว',
    sub_granted: 'เปิดสิทธิ์สมาชิกแล้ว',
    grant_title: 'มอบสิทธิ์สมาชิก',
    grant_user_id_ph: 'รหัสผู้ใช้ (UUID)',
    grant_pick_plan: 'เลือกแพ็กเกจ…',
    grant_period_end_hint: 'ไม่บังคับ: วันที่สิทธิ์หมด เว้นว่างเพื่อใช้ค่าเริ่มต้นของแพ็กเกจ',
    grant_btn: 'มอบ',
    editor_title_new: 'แพ็กเกจสมาชิกใหม่',
    editor_title_edit: 'แก้ไขแพ็กเกจสมาชิก',
    field_slug: 'Slug',
    field_name: 'ชื่อ',
    field_description: 'คำอธิบาย',
    field_price: 'ราคา',
    field_currency: 'สกุลเงิน',
    field_interval: 'รอบบิล',
    field_features: 'ฟีเจอร์ (JSON object)',
    field_features_hint: 'ใช้คีย์ feature_label_* สำหรับฟีเจอร์ที่รู้จัก เช่น {"no_ads": true, "max_collections": 50}',
    field_sort_order: 'ลำดับ',
  },
};
